import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { useToast } from '../../components/shared/Toast';
import { MdAdd, MdSearch, MdClose, MdEdit } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { ClearFiltersButton, erpPageBreadcrumbClass, erpPageBreadcrumbCurrentClass, erpPageBreadcrumbItemClass, erpPageBreadcrumbSepClass, PageSkeleton } from '@euroasiann/shared/ui';

// Types
interface Vessel {
    _id: string;
    name: string;
    organizationId?: string | { _id: string; name?: string };
}

interface Organization {
    _id: string;
    name: string;
    type?: string;
}

interface Risk {
    _id: string;
    riskId: string;
    vesselId?: Vessel;
    customerId?: Organization;
    riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
    probability: 'Low' | 'Medium' | 'High';
    impact: 'Low' | 'Medium' | 'High';
    description: string;
    dateIdentified: string;
    status: 'Open' | 'Mitigated' | 'Closed';
}

export function RiskManagementPage() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const [activeFilter, setActiveFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRisk, setEditingRisk] = useState<Risk | null>(null);

    const [formData, setFormData] = useState({
        vesselId: '',
        customerId: '',
        riskLevel: 'Low',
        probability: 'Low',
        impact: 'Low',
        description: '',
        status: 'Open',
        dateIdentified: new Date().toISOString().split('T')[0],
    });

    // Fetch Risks
    const { data: risksData, isLoading } = useQuery<Risk[]>({
        queryKey: ['admin-risks'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/admin/risks`);
            if (!response.ok) throw new Error('Failed to fetch risks');
            const data = await response.json();
            // Robust extraction for paginated or direct array responses
            return data.data?.data ?? data.data ?? [];
        },
    });

    // Fetch Vessels for Dropdown
    const { data: vesselsData } = useQuery<Vessel[]>({
        queryKey: ['admin-vessels-all'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/admin/vessels`);
            if (!response.ok) return [];
            const data = await response.json();
            // Robust extraction for paginated or direct array responses
            return data.data?.data ?? data.data ?? [];
        },
    });

    // Fetch Organizations (Customers) for Dropdown
    const { data: organizationsData } = useQuery<Organization[]>({
        queryKey: ['admin-organizations-all'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/admin/organizations`);
            if (!response.ok) return [];
            const data = await response.json();
            // Robust extraction for paginated or direct array responses
            return data.data?.data ?? data.data ?? [];
        },
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            const payload = {
                vesselId: data.vesselId,
                customerId: data.customerId || undefined,
                riskLevel: data.riskLevel,
                probability: data.probability,
                impact: data.impact,
                description: data.description,
                status: data.status,
                dateIdentified: new Date(data.dateIdentified).toISOString(),
            };

            const response = await authenticatedFetch('/api/v1/admin/risks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || err.error || 'Failed to create risk');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-risks'] });
            showToast('Risk created successfully', 'success');
            handleCloseModal();
        },
        onError: (error: any) => {
            showToast(error.message, 'error');
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
            const payload = {
                ...(data.vesselId && { vesselId: data.vesselId }),
                ...(data.customerId !== undefined && { customerId: data.customerId || undefined }),
                ...(data.riskLevel && { riskLevel: data.riskLevel }),
                ...(data.probability && { probability: data.probability }),
                ...(data.impact && { impact: data.impact }),
                ...(data.description !== undefined && { description: data.description }),
                ...(data.status && { status: data.status }),
                ...(data.dateIdentified && { dateIdentified: new Date(data.dateIdentified).toISOString() }),
            };

            const response = await authenticatedFetch(`/api/v1/admin/risks/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Failed to update risk');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-risks'] });
            showToast('Risk updated successfully', 'success');
            handleCloseModal();
        },
        onError: (error: any) => {
            showToast(error.message, 'error');
        },
    });

    // Derived state
    const filteredRisks = useMemo(() => {
        let baseData = risksData || [];

        if (activeFilter !== 'all') {
            baseData = baseData.filter(r => r.status.toLowerCase() === activeFilter.toLowerCase());
        }

        if (!searchQuery.trim()) return baseData;

        const query = searchQuery.toLowerCase();
        return baseData.filter(
            (risk) =>
                risk.riskId?.toLowerCase().includes(query) ||
                risk.vesselId?.name?.toLowerCase().includes(query) ||
                risk.customerId?.name?.toLowerCase().includes(query) ||
                risk.description?.toLowerCase().includes(query)
        );
    }, [risksData, searchQuery, activeFilter]);

    const customerOrganizations = useMemo(
        () =>
            (organizationsData || []).filter(
                (org) => !org.type || org.type === 'customer' || org.type === 'Customer'
            ),
        [organizationsData]
    );

    const resolveVesselOrganizationId = (vessel?: Vessel): string => {
        if (!vessel?.organizationId) return '';
        if (typeof vessel.organizationId === 'string') return vessel.organizationId;
        return vessel.organizationId._id || '';
    };

    const handleVesselChange = (vesselId: string) => {
        const vessel = (vesselsData || []).find((v) => v._id === vesselId);
        const orgId = resolveVesselOrganizationId(vessel);
        setFormData((prev) => ({
            ...prev,
            vesselId,
            customerId: orgId || prev.customerId,
        }));
    };

    const filters = [
        { id: 'all', label: 'All Risks' },
        { id: 'open', label: 'Open' },
        { id: 'mitigated', label: 'Mitigated' },
        { id: 'closed', label: 'Closed' },
    ];

    // Logic
    const handleOpenModal = (risk?: Risk) => {
        if (risk) {
            setEditingRisk(risk);
            setFormData({
                vesselId: risk.vesselId?._id || '',
                customerId: risk.customerId?._id || '',
                riskLevel: risk.riskLevel,
                probability: risk.probability,
                impact: risk.impact,
                description: risk.description || '',
                status: risk.status,
                dateIdentified: risk.dateIdentified
                    ? new Date(risk.dateIdentified).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0],
            });
        } else {
            setEditingRisk(null);
            setFormData({
                vesselId: '',
                customerId: '',
                riskLevel: 'Low',
                probability: 'Low',
                impact: 'Low',
                description: '',
                status: 'Open',
                dateIdentified: new Date().toISOString().split('T')[0],
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRisk(null);
    };

    const handleSubmit = () => {
        if (editingRisk) {
            updateMutation.mutate({ id: editingRisk._id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const columns = [
        {
            key: 'riskId',
            header: 'Risk ID',
            render: (risk: Risk) => (
                <span className="font-semibold text-[hsl(var(--primary))]">{risk.riskId}</span>
            ),
        },
        {
            key: 'vesselId',
            header: 'Vessel',
            render: (risk: Risk) => (
                <span className="text-[hsl(var(--foreground))]">{risk.vesselId?.name || 'N/A'}</span>
            ),
        },
        {
            key: 'customer',
            header: 'Customer',
            render: (risk: Risk) => (
                <span className="text-[hsl(var(--muted-foreground))]">{risk.customerId?.name || 'N/A'}</span>
            ),
        },
        {
            key: 'riskLevel',
            header: 'Level',
            render: (risk: Risk) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${risk.riskLevel === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400' :
                    risk.riskLevel === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400' :
                        risk.riskLevel === 'Medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400' :
                            'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400'
                    }`}>
                    {risk.riskLevel}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (risk: Risk) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${risk.status === 'Closed' ? 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-slate-800 dark:text-[#0088cc]/65' :
                    risk.status === 'Mitigated' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400'
                    }`}>
                    {risk.status}
                </span>
            ),
        },
        {
            key: 'dateIdentified',
            header: 'Date',
            render: (risk: Risk) => (
                <span className="text-[hsl(var(--muted-foreground))]">
                    {risk.dateIdentified ? new Date(risk.dateIdentified).toLocaleDateString() : 'N/A'}
                </span>
            ),
        },
        {
            key: 'actions',
            header: 'Action',
            render: (risk: Risk) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleOpenModal(risk)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                    >
                        <MdEdit className="w-5 h-5" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full min-h-screen p-8">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
                        <span className={erpPageBreadcrumbItemClass}>Admin</span>
                        <span className={erpPageBreadcrumbSepClass}>/</span>
                        <span className={erpPageBreadcrumbCurrentClass}>Risk Management</span>
                    </nav>
                    <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Risks</h1>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-lg transition-colors"
                >
                    <MdAdd className="w-5 h-5" />
                    Create Risk
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6 flex gap-2 overflow-x-auto">
                {filters.map((filter) => (
                    <button
                        key={filter.id}
                        onClick={() => setActiveFilter(filter.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === filter.id
                            ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                            : 'bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]'
                            }`}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {/* Search Bar */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--muted-foreground))] w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by ID, vessel, customer, or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    />
                </div>
                <ClearFiltersButton
                    visible={!!searchQuery.trim() || activeFilter !== 'all'}
                    onClick={() => {
                        setSearchQuery('');
                        setActiveFilter('all');
                    }}
                    variant="toolbar"
                />
            </div>

            {/* Table */}
            {isLoading ? (
                <PageSkeleton variant="table" />
            ) : (
                <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={filteredRisks}
                        emptyMessage="No risks found."
                    />
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md overflow-y-auto py-10">
                    <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-2xl mx-4 border border-[hsl(var(--border))] my-auto">
                        <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
                            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
                                {editingRisk ? 'Edit Risk' : 'Create New Risk'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            >
                                <MdClose className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                            {/* Vessel Input */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Vessel</label>
                                    <select
                                        value={formData.vesselId}
                                        onChange={(e) => handleVesselChange(e.target.value)}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="">Select Vessel</option>
                                        {(vesselsData || []).map((v) => (
                                            <option key={v._id} value={v._id}>{v.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Customer</label>
                                    <select
                                        value={formData.customerId}
                                        onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="">Select Customer</option>
                                        {customerOrganizations.map((org) => (
                                            <option key={org._id} value={org._id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Levels */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Risk Level</label>
                                    <select
                                        value={formData.riskLevel}
                                        onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value as typeof formData.riskLevel })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Probability</label>
                                    <select
                                        value={formData.probability}
                                        onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Impact</label>
                                    <select
                                        value={formData.impact}
                                        onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </div>
                            </div>

                            {/* Date & Status */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Open">Open</option>
                                        <option value="Mitigated">Mitigated</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Date Identified</label>
                                    <input
                                        type="date"
                                        value={formData.dateIdentified}
                                        onChange={(e) => setFormData({ ...formData, dateIdentified: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Description</label>
                                <textarea
                                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    rows={4}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Describe the risk..."
                                />
                            </div>

                        </div>

                        <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
                            <button
                                onClick={handleCloseModal}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-lg hover:bg-[hsl(var(--muted))] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-lg transition-colors flex items-center gap-2"
                            >
                                {(createMutation.isPending || updateMutation.isPending) && (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                )}
                                {editingRisk ? 'Update Risk' : 'Create Risk'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
